/* eslint-disable no-shadow */
import { router } from '@/appContext';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';

const initialState = {
  marketplaceParams: {
    s: [],
    l: [],
    f: [],
    p: '',
    o: '',
  },
  defaults: {
    // defaults to setup ui if there are no params
    defaultState: 'all',
    defaultSort: 'end_time',
  },
  persistedAfterLeave: {
    sort: '',
    savedSearchIds: [],
  },
  excludedLanes: [],
};

export const state = {
  ...initialState,
};

export const mutations = {
  setMarketplaceParams(state, marketplaceParams = {}) {
    state.marketplaceParams = marketplaceParams;
  },
  retainDefaults(state, { defaultState = 'all', defaultSort = 'end_time' }) {
    state.defaults = {
      ...state.defaults,
      defaultState,
      defaultSort,
    };
  },
  setPersistedAfterLeave(state, persistedAfterLeave = {}) {
    state.persistedAfterLeave = {
      ...state.persistedAfterLeave,
      ...persistedAfterLeave,
    };
  },
  handleRouterPush(state, validQueryToUse = {}) {
    // handles the router push on url changes from user actions
    router.push({
      query: validQueryToUse,
    });
  },

  setExcludedLanes(state, excludedLanes = []) {
    state.excludedLanes = excludedLanes;
  },
};

/*
* Each action for a marketplace param should already be validated to be applicable
*/
export const actions = {
  // when saved searches change, handle the router push for changes
  updateMarketplaceSavedSearchIds(
    { commit, rootState, state },
    { savedSearchIds = [] },
  ) {
    if (savedSearchIds.length) {
      // when saved searches change, remove quick search filters
      if (!isEqual(state.marketplaceParams.s, savedSearchIds)) {
        commit('setMarketplaceParams', {
          ...state.marketplaceParams,
          s: savedSearchIds,
          f: [],
        });

        commit('handleRouterPush', {
          ...omit(rootState.route.query, 'f'),
          s: JSON.stringify(savedSearchIds),
        });
      }
    } else if (rootState.route.query.s) {
      // if there's no more saved searches, remove from query
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        s: [],
      });

      commit('handleRouterPush', {
        ...omit(rootState.route.query, 's'),
      });
    }
  },

  // when lanes change, handle the router push for changes
  updateMarketplaceLanes({ commit, rootState, state }, { lanes = [] }) {
    if (lanes.length) {
      // when lanes change, include them
      if (!isEqual(state.marketplaceParams.l, lanes)) {
        commit('setMarketplaceParams', {
          ...state.marketplaceParams,
          l: lanes,
        });

        commit('handleRouterPush', {
          ...rootState.route.query,
          l: JSON.stringify(lanes),
        });
      }
    } else if (rootState.route.query.l) {
      // if there's no more lanes, remove from query
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        l: [],
      });

      commit('handleRouterPush', {
        ...omit(rootState.route.query, 'l'),
      });
    }
  },

  // when quick search filters change, handle the router push for changes
  updateMarketplaceQuickSearchFilters(
    { commit, rootState, state },
    { filters = [] },
  ) {
    if (filters.length) {
      // when filters change, remove saved searches
      if (!isEqual(state.marketplaceParams.f, filters)) {
        commit('setMarketplaceParams', {
          ...state.marketplaceParams,
          f: filters,
          s: [],
        });

        const filtersInUrl = filters.reduce((arr, f) => {
          let filter = f;

          if (filter.filterType === 'image') {
            // change the image value to the name display to not bulk browser url
            filter = {
              filterType: filter.filterType,
              value: filter.display.split('--')[0],
            };
          }
          arr.push(filter);
          return arr;
        }, []);

        commit('handleRouterPush', {
          ...omit(rootState.route.query, 's'),
          f: JSON.stringify(filtersInUrl),
        });
      }
    } else if (rootState.route.query.f) {
      // if there's no more filters, remove from query
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        f: [],
      });

      commit('handleRouterPush', {
        ...omit(rootState.route.query, 'f'),
      });
    }
  },

  // when state filter changes, change the sort order if necessary
  // handle the router push for changes
  updateMarketplaceStateFilter(
    { commit, rootState, state },
    { stateFilter = '', sortOptionsForChangedState = [] },
  ) {
    if (stateFilter) {
      let sortOrderToUse = state.marketplaceParams.o;

      if (
        sortOptionsForChangedState.length
        && !sortOptionsForChangedState.includes(state.marketplaceParams.o)
      ) {
        // if the sort order is not in the new state, set it to the first one
        [sortOrderToUse] = sortOptionsForChangedState;
      }

      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        p: stateFilter,
        o: sortOrderToUse || state.defaults.defaultSort,
        l: stateFilter === 'sold' ? [] : state.marketplaceParams.l, // clear lanes if sold state
      });

      commit('handleRouterPush', {
        ...rootState.route.query,
        p: stateFilter,
        o: sortOrderToUse || state.defaults.defaultSort,
        l: stateFilter === 'sold' ? undefined : rootState.route.query.l, // clear lanes if sold state
      });
    }
  },

  // when sort order changes, handle the router push for changes
  updateMarketplaceSortOrder({ commit, rootState, state }, { sortOrder = '' }) {
    if (sortOrder) {
      commit('setMarketplaceParams', {
        ...state.marketplaceParams,
        p: state.marketplaceParams.p || state.defaults.defaultState,
        o: sortOrder,
      });

      commit('handleRouterPush', {
        ...rootState.route.query,
        p: state.marketplaceParams.p || state.defaults.defaultState,
        o: sortOrder,
      });
    }
  },

  // general update of marketplace params, handle router push conditionally
  updateMarketplaceParams(
    { commit, rootState, state },
    { marketplaceParams = {}, routerPush = false },
  ) {
    // combine existing with what was given
    const validParams = {
      ...state.marketplaceParams,
      ...marketplaceParams,
    };

    commit('setMarketplaceParams', validParams);

    if (routerPush) {
      const validQuery = Object.keys(validParams).reduce((obj, key) => {
        const validObj = obj;

        if (['s', 'l', 'f'].includes(key) && validParams?.[key]?.length) {
          validObj[key] = JSON.stringify(validParams[key]);
        } else if (['p', 'o'].includes(key) && validParams?.[key]) {
          validObj[key] = validParams[key];
        }

        return obj;
      }, {});

      // handle bulk router push
      commit('handleRouterPush', {
        ...omit(rootState.route.query, ['s', 'l', 'f', 'p', 'o']),
        ...validQuery,
      });
    }
  },

  // reset the marketplace params to initial values
  resetMarketplaceParams({ commit, dispatch, state }, { persistAfterLeave = false } = {}) {
    if (persistAfterLeave) {
      commit('setPersistedAfterLeave', {
        sort: state.marketplaceParams.o,
        savedSearchIds: state.marketplaceParams.s,
      });
    }

    commit('setMarketplaceParams', {
      ...initialState.marketplaceParams,
      p: state.defaults.defaultState,
      o: state.persistedAfterLeave.sort || state.defaults.defaultSort,
      s: state.persistedAfterLeave.savedSearchIds || initialState.marketplaceParams.s,
    });

    // also clear excluded lanes
    dispatch('updateExcludedLanes', {
      excludedLanes: [],
    });
  },

  updateExcludedLanes({ commit }, { excludedLanes = [] }) {
    commit('setExcludedLanes', excludedLanes);
  },

  persistDefaults(
    { commit },
    { defaultState = 'all', defaultSort = 'end_time' },
  ) {
    commit('retainDefaults', { defaultState, defaultSort });
  },
};

export const getters = {
  marketplaceSavedSearchIds: ({ marketplaceParams }) => marketplaceParams.s,
  marketplaceLanes: ({ marketplaceParams }) => marketplaceParams.l,
  marketplaceQuickSearchFilters: ({ marketplaceParams }) => marketplaceParams.f,
  marketplaceStateFilter: ({ marketplaceParams }) => marketplaceParams.p,
  marketplaceSortOrder: ({ marketplaceParams }) => marketplaceParams.o,
  marketplaceParams: ({ marketplaceParams }) => marketplaceParams,
  excludedLanes: ({ excludedLanes }) => excludedLanes,
};

const marketplaceRouter = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default marketplaceRouter;
